import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { showErrorToast, showSuccessToast } from '../../utils/notificationUtils';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { inpatientAPI } from "../../services/api";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { useTranslation } from "react-i18next";
import PrescriptionEntries from '../shared/PrescriptionEntries';


const InpatientCheckoutModal = ({
  isOpen,
  onClose,
  inpatientId,
  onSave,
  checkoutForm: propCheckoutForm,
  onFormChange: propOnFormChange,
  workingHours = [],
  initializing = false
}) => {
  const [localForm, setLocalForm] = useState({
    finalDiagnosis: '',
    doctorNotes: '',
    prescription: '',
    followUpRequired: false,
    followUpDate: '',
    followUpTime: '',
    followUpType: '3',
    initialDiagnosis: ''
  });

  const checkoutForm = propCheckoutForm || localForm;

  const handleFormChange = useCallback((field, value) => {
    if (propOnFormChange) {
      propOnFormChange(field, value);
    } else {
      setLocalForm(prev => ({ ...prev, [field]: value }));
    }
  }, [propOnFormChange]);

  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const formatDate = useCallback(
    (value) => {
      if (!value) return "";
      try {
        return new Date(value).toLocaleDateString(i18n.language, {
          year: "numeric",
          month: "short",
          day: "numeric",
          calendar: "gregory"
        });
      } catch {
        return value;
      }
    },
    [i18n.language]
  );

  const [loading, setLoading] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);


  // Generate time slots based on working hours
  const generateTimeSlots = useCallback((workingHours) => {
    if (!workingHours || workingHours.length === 0) return [];

    const slots = [];
    workingHours.forEach(wh => {
      if (!wh.enabled) return; // skip disabled days

      const startTime = wh.from;
      const endTime = wh.to;

      // Convert time strings to minutes for easier calculation
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);

      if (startMinutes === null || endMinutes === null || startMinutes >= endMinutes) {
        console.warn('Invalid time range:', startTime, 'to', endTime);
        return;
      }

      // Generate 30-minute slots
      for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
        const timeString = minutesToTime(minutes);
        slots.push({
          time: timeString,
          display: timeString,
          day: dayStringToIndex(wh.day)
        });
      }
    });

    return slots.sort((a, b) => a.time.localeCompare(b.time));
  }, []);

  // Helper functions for time conversion
  const timeToMinutes = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
  };

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Normalize workingHours day values to Date.getDay() index (0=Sun..6=Sat)
  const dayStringToIndex = (inputDay) => {
    if (inputDay === undefined || inputDay === null) return undefined;
    if (typeof inputDay === 'number' && inputDay >= 0 && inputDay <= 6) return inputDay;
    const d = String(inputDay).toLowerCase();
    const fullMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sun: 0,
      mon: 1,
      tue: 2,
      wed: 3,
      thu: 4,
      fri: 5,
      sat: 6
    };
    if (!isNaN(d) && d.trim() !== "") {
      const num = parseInt(d);
      if (num >= 0 && num <= 6) return num;
    }
    return fullMap[d];
  };


  // Refs for Flatpickr
  const dateInputRef = useRef(null);
  const datePickerRef = useRef(null);
  const shouldOpenAfterInitRef = useRef(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Handle keyboard events and body scroll
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  const fetchLatestDiagnosis = useCallback(async () => {
    if (!isOpen || !inpatientId || checkoutForm.finalDiagnosis) return;

    try {
      const response = await inpatientAPI.getSoapNoteByInpatientId(inpatientId);
      // Handle array response
      const soapData = (response.data && Array.isArray(response.data.data))
        ? response.data.data[0]
        : (Array.isArray(response.data) ? response.data[0] : response.data);

      // If data is wrapped in 'data' property check
      const actualData = soapData && soapData.data ? soapData.data : soapData;

      let shouldUpdate = false;
      let finalDiag = '';

      if (actualData) {
        // 1. Try initial_diagnosis first (current input)
        if (actualData.initial_diagnosis) {
          finalDiag = actualData.initial_diagnosis;
          shouldUpdate = true;
        }
        // 2. Fallback to explicit final_diagnosis
        else if (actualData.final_diagnosis) {
          finalDiag = actualData.final_diagnosis;
          shouldUpdate = true;
        }
        // 3. Fallback to calculating from history
        else if (actualData.diagnosis_history) {
          try {
            const history = typeof actualData.diagnosis_history === 'string'
              ? JSON.parse(actualData.diagnosis_history)
              : actualData.diagnosis_history;

            if (Array.isArray(history) && history.length > 0) {
              // Latest diagnosis is at the end of the array based on my unified history management
              finalDiag = history[history.length - 1].diagnosis;
              shouldUpdate = true;
            }
          } catch (e) { console.error('Error parsing diag history', e); }
        }
      }

      if (shouldUpdate && finalDiag) {
        handleFormChange('finalDiagnosis', finalDiag);
      }
    } catch (error) {
      // Silent fail is okay, user can type manually
      console.warn('Failed to fetch auto-diagnosis', error);
    }
  }, [isOpen, inpatientId, checkoutForm.finalDiagnosis, handleFormChange]);

  useEffect(() => {
    fetchLatestDiagnosis();
  }, [fetchLatestDiagnosis]);



  const handleSubmit = async () => {
    if (!checkoutForm.finalDiagnosis.trim()) {
      showErrorToast(t("receptionInpatients.checkout.validation.finalDiagnosisRequired"));
      return;
    }

    if (checkoutForm.followUpRequired) {
      if (!checkoutForm.followUpDate) {
        showErrorToast(t("receptionInpatients.checkout.validation.followUpDateRequired"));
        return;
      }
      if (!checkoutForm.followUpTime) {
        showErrorToast(t("receptionInpatients.checkout.validation.followUpTimeRequired"));
        return;
      }
    }

    setLoading(true);
    try {
      // Prepare checkout data for the API
      const checkoutData = {
        final_diag: checkoutForm.finalDiagnosis,
        doctor_notes: checkoutForm.doctorNotes,
        prescription: checkoutForm.prescription,
        follow_up_required: checkoutForm.followUpRequired,
        follow_up_date: checkoutForm.followUpDate,
        follow_up_time: checkoutForm.followUpTime,
        follow_up_type: checkoutForm.followUpType,
        initial_diag: checkoutForm.initialDiagnosis || null,
        status: 'checkout' // Mark as checkout for inpatient
      };

      // Update the inpatient visit with checkout data
      if (inpatientId) {
        await inpatientAPI.updateInpatientVisit(inpatientId, checkoutData);
      } else {
        throw new Error(t("receptionInpatients.checkout.toasts.missingId"));
      }

      showSuccessToast(t("receptionInpatients.checkout.toasts.success"));
      // Prefer notifying parent to handle completion (e.g., close overlay and other modals)
      if (typeof onSave === 'function') {
        try {
          await onSave(checkoutData);
        } catch {
          // Even if parent throws, ensure modal closes to avoid trap state
          onClose && onClose();
        }
      } else {
        onClose && onClose();
      }
    } catch (error) {
      console.error('Error during inpatient checkout:', error);
      const message = error?.message || error?.response?.data?.message;
      showErrorToast(message || t("receptionInpatients.checkout.toasts.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  const initializeDatePicker = useCallback(() => {
    if (!isOpen || !checkoutForm.followUpRequired || !dateInputRef.current) return;

    // Preserve intent to open across re-inits (e.g., when workingHours arrive)
    const wasOpen = datePickerRef.current && datePickerRef.current.isOpen;
    const inputIsFocused = document.activeElement === dateInputRef.current;
    const shouldReopen = shouldOpenAfterInitRef.current || wasOpen || inputIsFocused;

    // Destroy any existing instance
    if (datePickerRef.current) {
      try { datePickerRef.current.destroy(); } catch { /* ignore destroy error */ }
    }

    // Create array of available days based on working hours
    const availableDays = workingHours && workingHours.length > 0
      ? workingHours
        .filter(wh => wh.enabled !== false && wh.day && wh.from && wh.to)
        .map(wh => dayStringToIndex(wh.day))
        .filter((idx) => idx !== undefined)
      : [];

    // Enable working hours restriction and disable past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const disableFunction = [
      function (date) {
        // Disable past dates
        const isPastDate = date < today;
        return isPastDate;
      }
    ];

    // If we have working hours, also restrict to available days
    if (availableDays.length > 0) {
      disableFunction.push(function (date) {
        const dayOfWeek = date.getDay();
        const isAvailableDay = availableDays.includes(dayOfWeek);
        return !isAvailableDay;
      });
    }

    try {
      datePickerRef.current = flatpickr(dateInputRef.current, {
        dateFormat: 'Y-m-d',
        minDate: 'today',
        disable: disableFunction,
        clickOpens: true,
        allowInput: false,
        defaultDate: checkoutForm.followUpDate || null,
        appendTo: document.body,
        onReady: function (selectedDates, dateStr, instance) {
          if (shouldReopen) {
            setTimeout(() => { try { instance.open(); } catch { /* ignore open error */ } }, 0);
          }
        },
        onChange: function (selectedDates, dateStr) {
          handleFormChange('followUpDate', dateStr);
          handleFormChange('followUpTime', ''); // Reset time when date changes
          // Close time dropdown when date changes
          setShowTimeDropdown(false);
        }
      });
      if (shouldReopen) {
        try { datePickerRef.current.open(); } catch { /* ignore open error */ }
        shouldOpenAfterInitRef.current = false;
      }
    } catch (error) {
      console.error('Error initializing Flatpickr:', error);
    }
  }, [isOpen, checkoutForm.followUpRequired, checkoutForm.followUpDate, workingHours, handleFormChange]);

  // Initialize Flatpickr for date picker
  useEffect(() => {
    if (isOpen && checkoutForm.followUpRequired && dateInputRef.current) {
      const timeoutId = setTimeout(initializeDatePicker, 100);
      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (datePickerRef.current) {
        try {
          datePickerRef.current.destroy();
        } catch (error) {
          console.error('Error destroying Flatpickr:', error);
        }
      }
    };
  }, [isOpen, workingHours, checkoutForm.followUpRequired, initializeDatePicker]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTimeDropdown && !event.target.closest('[data-time-dropdown]')) {
        setShowTimeDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showTimeDropdown]);

  // Generate time slots when working hours change
  useEffect(() => {
    if (workingHours && workingHours.length > 0) {
      const slots = generateTimeSlots(workingHours);
      setAvailableTimeSlots(slots);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [workingHours, generateTimeSlots]);


  // Filter time slots based on selected date and current time
  // - For future dates: show all available time slots
  // - For today: show only time slots that are 15+ minutes in the future
  const getAvailableTimeSlotsForDate = (selectedDate) => {
    if (!selectedDate) {
      return availableTimeSlots;
    }

    const selectedDateObj = new Date(selectedDate);
    const dayOfWeek = selectedDateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Convert day number to day name
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek];

    // Check if selected date is today
    const today = new Date();
    const isToday = selectedDateObj.toDateString() === today.toDateString();

    // Filter slots for the selected day
    let filteredSlots = availableTimeSlots.filter(slot =>
      slot.day === dayOfWeek
    );

    if (isToday) {
      // Filter out past time slots for today
      const currentHour = today.getHours();
      const currentMinute = today.getMinutes();
      const currentTimeInMinutes = currentHour * 60 + currentMinute;

      filteredSlots = filteredSlots.filter(slot => {
        const [slotHour, slotMinute] = slot.time.split(':').map(Number);
        const slotTimeInMinutes = slotHour * 60 + slotMinute;

        // Show time slots that are at least 15 minutes in the future
        const isFutureSlot = slotTimeInMinutes >= (currentTimeInMinutes + 15);

        return isFutureSlot;
      });
    }

    return filteredSlots;
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="inpatient-checkout-modal-overlay"
      dir={i18n.dir()}
      lang={i18n.language}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'grid',
        placeItems: 'center',
        zIndex: 10000,
        padding: '16px',
        fontFamily: '"Red Hat Display", sans-serif',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '20px',
          padding: '32px',
          maxWidth: 'min(800px, 90vw)',
          width: 'min(800px, 90vw)',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transform: 'scale(1) translateY(0px) rotateX(0deg)',
          opacity: 1,
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          transformOrigin: 'center center'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #F0FDF4 0%, #ECFDF5 100%)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.1)'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: '#1F2937',
                  margin: 0,
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {loading || initializing
                  ? t("receptionInpatients.checkout.header.processing")
                  : t("receptionInpatients.checkout.header.title")}
              </h2>
              {(loading || initializing) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '4px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #22C55E',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <span style={{
                    fontSize: '12px',
                    color: '#6B7280'
                  }}>
                    {t("receptionInpatients.checkout.header.loading")}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: loading ? 'not-allowed' : 'pointer',
              color: '#6B7280',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
                e.target.style.color = '#EF4444';
                e.target.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6B7280';
                e.target.style.transform = 'scale(1)';
              }
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Final Diagnosis */}
            <div style={{
              background: '#F9FAFB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB'
            }}>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 16px 0',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t("receptionInpatients.checkout.sections.finalDiagnosis.title")}
              </h3>
              <textarea
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'all 0.2s',
                  backgroundColor: '#FFFFFF'
                }}
                value={checkoutForm.finalDiagnosis}
                onChange={(e) => handleFormChange('finalDiagnosis', e.target.value)}
                placeholder={t("receptionInpatients.checkout.sections.finalDiagnosis.placeholder", { defaultValue: "Enter final diagnosis..." })}
                disabled={loading || initializing}
              />
            </div>

            {/* Doctor Notes */}
            <div style={{
              background: '#F9FAFB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB'
            }}>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 16px 0',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t("receptionInpatients.checkout.sections.doctorNotes.title")}
              </h3>
              <textarea
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'all 0.2s',
                  backgroundColor: '#FFFFFF'
                }}
                value={checkoutForm.doctorNotes}
                onChange={(e) => handleFormChange('doctorNotes', e.target.value)}
                placeholder={t("receptionInpatients.checkout.sections.doctorNotes.placeholder")}
                disabled={loading || initializing}
              />
            </div>

            {/* Prescription */}
            <div style={{
              background: '#F9FAFB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB'
            }}>
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 16px 0',
                  textAlign: isRTL ? 'right' : 'left'
                }}
              >
                {t("receptionInpatients.checkout.sections.prescription.title")}
              </h3>
              <PrescriptionEntries
                value={checkoutForm.prescription}
                onChange={(value) => handleFormChange('prescription', value)}
                disabled={loading || initializing}
              />
            </div>

            {/* Follow-up Section */}
            <div style={{
              background: '#F9FAFB',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                flexDirection: isRTL ? 'row-reverse' : 'row',
                justifyContent: isRTL ? 'flex-end' : 'flex-start'
              }}>
                <input
                  id="followUpRequired"
                  type="checkbox"
                  checked={checkoutForm.followUpRequired}
                  onChange={(e) => handleFormChange('followUpRequired', e.target.checked)}
                  disabled={loading || initializing}
                  style={{
                    width: '20px',
                    height: '20px',
                    accentColor: '#22C55E'
                  }}
                />
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start',
                  width: '100%',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#111827',
                  cursor: 'pointer',
                  textAlign: isRTL ? 'right' : 'left'
                }}
                  htmlFor="followUpRequired"
                >
                  {t("receptionInpatients.checkout.sections.followUp.label")}
                </label>
              </div>

              {checkoutForm.followUpRequired && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
                    <div>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        justifyContent: isRTL ? 'flex-end' : 'flex-start',
                        textAlign: isRTL ? 'right' : 'left',
                        flexDirection: isRTL ? 'row-reverse' : 'row'
                      }}>
                        <span aria-hidden="true">📅</span>
                        <span>{t("receptionInpatients.checkout.sections.followUp.dateLabel")}</span>
                      </span>
                      <input
                        ref={dateInputRef}
                        type="text"
                        className="reception-modal-input"
                        placeholder={t("receptionInpatients.checkout.sections.followUp.datePlaceholder")}
                        style={{
                          width: '100%',
                          cursor: 'pointer',
                          textAlign: isRTL ? 'right' : 'left',
                          direction: isRTL ? 'rtl' : 'ltr'
                        }}
                        disabled={loading || initializing}
                        onClick={() => {
                          if (!datePickerRef.current) {
                            shouldOpenAfterInitRef.current = true;
                            initializeDatePicker();
                            // Open after init in next tick
                            setTimeout(() => {
                              try { datePickerRef.current && datePickerRef.current.open(); } catch { /* ignore */ }
                            }, 0);
                            return;
                          }
                          try { datePickerRef.current.open(); } catch { /* ignore */ }
                        }}
                        onFocus={() => {
                          // If init hasn't happened yet but user focuses, ensure it opens after init
                          if (!datePickerRef.current) {
                            shouldOpenAfterInitRef.current = true;
                          }
                        }}
                        onMouseDown={() => {
                          // Capture mouse down before focus to mark intent
                          if (!datePickerRef.current) {
                            shouldOpenAfterInitRef.current = true;
                          }
                        }}
                        readOnly
                      />
                    </div>

                    <div>
                      <span style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '8px',
                        justifyContent: isRTL ? 'flex-end' : 'flex-start',
                        textAlign: isRTL ? 'right' : 'left',
                        flexDirection: isRTL ? 'row-reverse' : 'row'
                      }}>
                        <span aria-hidden="true">🕐</span>
                        <span>{t("receptionInpatients.checkout.sections.followUp.timeLabel")}</span>
                      </span>
                      <div style={{ position: 'relative' }} data-time-dropdown>
                        <div
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '16px',
                            transition: 'all 0.2s',
                            outline: 'none',
                            cursor: checkoutForm.followUpDate ? 'pointer' : 'not-allowed',
                            backgroundColor: checkoutForm.followUpDate ? '#FFFFFF' : '#F9FAFB',
                            opacity: checkoutForm.followUpDate ? 1 : 0.6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            direction: isRTL ? 'rtl' : 'ltr'
                          }}
                          onClick={() => {
                            if (!checkoutForm.followUpDate) {
                              return; // Don't open if no date selected
                            }
                            setShowTimeDropdown(!showTimeDropdown);
                          }}
                        >
                          <span style={{ color: checkoutForm.followUpTime ? '#111827' : '#9ca3af', textAlign: isRTL ? 'right' : 'left', flex: 1 }}>
                            {!checkoutForm.followUpDate
                              ? t("receptionInpatients.checkout.sections.followUp.timePlaceholderDisabled")
                              : (checkoutForm.followUpTime || t("receptionInpatients.checkout.sections.followUp.timePlaceholder"))}
                          </span>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>
                            {showTimeDropdown ? '▲' : '▼'}
                          </span>
                        </div>

                        {/* Time Dropdown Content */}
                        {showTimeDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            zIndex: 10,
                            maxHeight: '250px',
                            overflow: 'auto',
                            marginTop: '4px'
                          }}>
                            {(() => {
                              const availableSlots = getAvailableTimeSlotsForDate(checkoutForm.followUpDate);

                              if (availableSlots.length === 0) {
                                return (
                                  <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#6B7280',
                                    fontSize: '14px',
                                    backgroundColor: '#FEF3C7',
                                    borderRadius: '8px',
                                    margin: '8px',
                                    direction: isRTL ? 'rtl' : 'ltr'
                                  }}>
                                    {checkoutForm.followUpDate ?
                                      (() => {
                                        const selectedDateObj = new Date(checkoutForm.followUpDate);
                                        const today = new Date();
                                        const isSelectedDateToday = selectedDateObj.toDateString() === today.toDateString();
                                        return isSelectedDateToday
                                          ? t("receptionInpatients.checkout.sections.followUp.noSlotsToday")
                                          : t("receptionInpatients.checkout.sections.followUp.noSlotsDay");
                                      })() :
                                      t("receptionInpatients.checkout.sections.followUp.selectDateFirst")
                                    }
                                  </div>
                                );
                              }

                              return (
                                <>
                                  <div style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#dcfce7',
                                    borderBottom: '1px solid #dcfce7',
                                    fontSize: '12px',
                                    color: '#166534',
                                    fontWeight: '500',
                                    textAlign: isRTL ? 'right' : 'left',
                                    direction: isRTL ? 'rtl' : 'ltr'
                                  }}>
                                    {t("receptionInpatients.checkout.sections.followUp.availableSlots", {
                                      date: formatDate(checkoutForm.followUpDate)
                                    })}
                                  </div>
                                  {availableSlots.map((slot, index) => (
                                    <div
                                      key={index}
                                      style={{
                                        cursor: "pointer",
                                        padding: "12px 16px",
                                        borderBottom: '1px solid #f3f4f6',
                                        transition: 'background-color 0.2s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: slot.time === checkoutForm.followUpTime ? '#dcfce7' : 'transparent',
                                        borderLeft: !isRTL && slot.time === checkoutForm.followUpTime ? '3px solid #22c55e' : '3px solid transparent',
                                        borderRight: isRTL && slot.time === checkoutForm.followUpTime ? '3px solid #22c55e' : '3px solid transparent',
                                        color: slot.time === checkoutForm.followUpTime ? '#166534' : '#111827',
                                        fontWeight: slot.time === checkoutForm.followUpTime ? '600' : '400',
                                        direction: isRTL ? 'rtl' : 'ltr'
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleFormChange('followUpTime', slot.time);
                                        setShowTimeDropdown(false);
                                      }}
                                      onMouseEnter={(e) => {
                                        if (slot.time !== checkoutForm.followUpTime) {
                                          e.target.style.backgroundColor = '#f9fafb';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (slot.time !== checkoutForm.followUpTime) {
                                          e.target.style.backgroundColor = 'transparent';
                                        }
                                      }}
                                    >
                                      <span style={{ fontSize: '16px', fontWeight: '500', textAlign: isRTL ? 'right' : 'left', width: '100%' }}>
                                        {slot.display}
                                      </span>
                                    </div>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#374151',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                      justifyContent: isRTL ? 'flex-end' : 'flex-start',
                      textAlign: isRTL ? 'right' : 'left',
                      flexDirection: isRTL ? 'row-reverse' : 'row'
                    }}>
                      <span aria-hidden="true">📋</span>
                      <span>{t("receptionInpatients.checkout.sections.followUp.typeLabel")}</span>
                    </span>
                    <select
                      className="reception-modal-input"
                      style={{
                        width: '100%',
                        cursor: 'pointer',
                        textAlign: isRTL ? 'right' : 'left',
                        textAlignLast: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr'
                      }}
                      value={checkoutForm.followUpType || "3"}
                      onChange={(e) => handleFormChange('followUpType', e.target.value)}
                      disabled={loading || initializing}
                    >
                      <option value="3">
                        {t("receptionInpatients.checkout.sections.followUp.typeOptionFollowUp")}
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: isRTL ? 'flex-start' : 'flex-end',
          gap: '12px',
          marginTop: '32px',
          paddingTop: '16px',
          borderTop: '1px solid #E5E7EB',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}>
          <button
            onClick={handleCancel}
            disabled={loading || initializing}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: '1px solid #D1D5DB',
              backgroundColor: '#FFFFFF',
              color: '#374151',
              fontSize: '16px',
              fontWeight: '500',
              cursor: (loading || initializing) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: (loading || initializing) ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}
            onMouseEnter={(e) => {
              if (!(loading || initializing)) {
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.borderColor = '#9CA3AF';
              }
            }}
            onMouseLeave={(e) => {
              if (!(loading || initializing)) {
                e.target.style.backgroundColor = '#FFFFFF';
                e.target.style.borderColor = '#D1D5DB';
              }
            }}
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || initializing || !checkoutForm.finalDiagnosis.trim()}
            style={{
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: (loading || initializing || !checkoutForm.finalDiagnosis.trim()) ? '#9CA3AF' : '#22C55E',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: '500',
              cursor: (loading || initializing || !checkoutForm.finalDiagnosis.trim()) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexDirection: isRTL ? 'row-reverse' : 'row'
            }}
            onMouseEnter={(e) => {
              if (!(loading || initializing) && checkoutForm.finalDiagnosis.trim()) {
                e.target.style.backgroundColor = '#16A34A';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(loading || initializing) && checkoutForm.finalDiagnosis.trim()) {
                e.target.style.backgroundColor = '#22C55E';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            {(loading || initializing) && (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #FFFFFF',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {(loading || initializing)
              ? t("receptionInpatients.checkout.buttons.processing")
              : t("receptionInpatients.checkout.buttons.submit")}
          </button>
        </div>

        {/* Add spinner animation */}
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            /* Custom scrollbar for webkit browsers */
            .inpatient-checkout-modal-overlay ::-webkit-scrollbar {
              width: 8px;
            }

            .inpatient-checkout-modal-overlay ::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 4px;
            }

            .inpatient-checkout-modal-overlay ::-webkit-scrollbar-thumb {
              background: linear-gradient(135deg, #cbd5e1, #94a3b8);
              border-radius: 4px;
              transition: background 0.2s ease;
            }

            .inpatient-checkout-modal-overlay ::-webkit-scrollbar-thumb:hover {
              background: linear-gradient(135deg, #94a3b8, #64748b);
            }

            /* Firefox scrollbar */
            .inpatient-checkout-modal-overlay {
              scrollbar-width: thin;
              scrollbar-color: #cbd5e1 #f1f5f9;
            }

            /* Ensure flatpickr calendar overlays the modal */
            .flatpickr-calendar {
              z-index: 100000 !important;
            }
          `}
        </style>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default InpatientCheckoutModal;
